import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import ReviewBar from "./components/ReviewBar";
import About from "./components/About";
import ToursSection from "./components/ToursSection";
import Guides from "./components/Guides";
import DiziTours from "./components/DiziTours";
import Testimonials from "./components/Testimonials";
import FeaturedVideo from "./components/FeaturedVideo";
import Media from "./components/Media";
import FAQ from "./components/FAQ";
import Footer, { CTA } from "./components/Footer";
import StickyWhatsApp from "./components/StickyWhatsApp";
import SEO from "./components/SEO";
import { Toaster } from "./components/ui/toaster";
import { I18nProvider } from "./lib/i18n";
import { AuthProvider } from "./lib/auth";
import { SettingsProvider, useSettings } from "./lib/settings";

import TourCategoryPage from "./pages/TourCategoryPage";
import AllToursPage from "./pages/AllToursPage";
import TourDetailPage from "./pages/TourDetailPage";
import AboutPage from "./pages/AboutPage";
import FAQPage from "./pages/FAQPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import DiziTourRequestPage from "./pages/DiziTourRequestPage";
import TailorMadeTourPage from "./pages/TailorMadeTourPage";
import ContactPage from "./pages/ContactPage";
import BlogList, { BlogPost } from "./pages/Blog";
import CustomPage from "./pages/CustomPage";
import ThankYouPage from "./pages/ThankYouPage";
import NotFoundPage from "./pages/NotFoundPage";
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
import SiteEditor from "./pages/SiteEditor";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Home = () => {
  const { settings } = useSettings();
  const s = settings || {};
  const orgName = s.seo_org_name || "Real Istanbul Tours";
  const base =
    s.seo_canonical_base ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const orgLd = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    name: orgName,
    url: base,
    logo: s.seo_favicon || s.seo_og_image || undefined,
    telephone: s.contact_phone || undefined,
    email: s.contact_email || undefined,
    address: s.meeting_point
      ? { "@type": "PostalAddress", addressLocality: "Istanbul", addressCountry: "TR", streetAddress: s.meeting_point }
      : undefined,
    sameAs: (s.review_platforms || [])
      .map((rp) => rp.url)
      .filter(Boolean),
    aggregateRating: s.review_average
      ? {
          "@type": "AggregateRating",
          ratingValue: String(s.review_average),
          reviewCount: String(s.review_count || 100),
          bestRating: "5",
        }
      : undefined,
  };
  const siteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: orgName,
    url: base,
    potentialAction: {
      "@type": "SearchAction",
      target: `${base}/tours/small-group?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
  return (
    <>
      <SEO
        title={s.seo_home_title || s.seo_site_title}
        description={s.seo_home_description || s.seo_meta_description}
        image={s.seo_home_og_image || s.seo_og_image}
        path="/"
        jsonLd={[orgLd, siteLd]}
      />
      <Hero />
      <ReviewBar />
      <About />
      <ToursSection />
      <DiziTours />
      <Guides />
      <Testimonials />
      <FeaturedVideo />
      <Media />
      <FAQ />
      <CTA />
    </>
  );
};

const PublicLayout = ({ children }) => (
  <div className="min-h-screen bg-[var(--ri-cream)] flex flex-col">
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
    <StickyWhatsApp />
  </div>
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SettingsProvider>
              <I18nProvider>
              <ScrollToTop />
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[var(--ri-cream)]" />}>
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/site" element={<SiteEditor />} />
                <Route path="/admin" element={<AdminLogin />} />

                <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                <Route path="/tour/:id" element={<PublicLayout><TourDetailPage /></PublicLayout>} />
                <Route path="/tours" element={<PublicLayout><AllToursPage /></PublicLayout>} />
                <Route path="/tours/:category" element={<PublicLayout><TourCategoryPage /></PublicLayout>} />
                <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
                <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
                <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
                <Route path="/terms-conditions" element={<PublicLayout><TermsPage /></PublicLayout>} />
                <Route path="/dizi-tour-request" element={<PublicLayout><DiziTourRequestPage /></PublicLayout>} />
                <Route path="/tailor-made-tour" element={<PublicLayout><TailorMadeTourPage /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
                <Route path="/blog" element={<PublicLayout><BlogList /></PublicLayout>} />
                <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
                <Route path="/page/:slug" element={<PublicLayout><CustomPage /></PublicLayout>} />
                <Route path="/thank-you" element={<PublicLayout><ThankYouPage /></PublicLayout>} />
                <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
              </Routes>
              </Suspense>
              <Toaster />
              </I18nProvider>
            </SettingsProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
